import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { flattenStateHistory } from '../lib/utils';
import type { Product, ProductSize } from './useProducts';

// ─── Types ───────────────────────────────────────────────────────────

type DateRange = '7d' | '30d' | '90d' | '12m';

interface AnalyticsOrder {
  id: string;
  state: string;
  shopperId: string;
  businessId: string;
  customerName?: string;
  items: {
    productId: string;
    name: string;
    brand: string;
    price: number;
    size: string;
    quantity: number;
    imageUrl?: string;
  }[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  payment?: {
    status: string;
    capturedAmount?: number;
    refundedAmount?: number;
  };
  returns?: {
    hasActiveReturn: boolean;
    returnRequestIds: string[];
    totalRefunded: number;
  };
  stateHistory?: Record<string, {
    fromState: string | null;
    toState: string;
    action: string;
    actorRole?: string;
    timestamp?: number;
  }>;
  createdAt: any;
  updatedAt?: any;
  deliveredAt?: any;
}

export interface KPIResult {
  label: string;
  value: string;
  change: number;
  changeLabel: string;
  sparkline: number[];
}

export interface DailyRevenue {
  day: string;
  revenue: number;
  orders: number;
}

export interface TopProductResult {
  id: string;
  name: string;
  image: string;
  category: string;
  unitsSold: number;
  revenue: number;
  trend: number;
  conversionRate: number;
}

export interface HourlyData {
  hour: number;
  orders: number;
}

export interface CustomerSegment {
  label: string;
  count: number;
  percentage: number;
  color: string;
}

export interface CategoryRevenue {
  name: string;
  revenue: number;
  percentage: number;
  color: string;
}

export interface LowStockItem {
  name: string;
  size: string;
  remaining: number;
  image: string;
}

export interface OrderStatusGroup {
  label: string;
  count: number;
  total: number;
  color: string;
}

export interface PerformanceMetric {
  label: string;
  value: string;
  target: string;
  achieved: boolean;
  barWidth: number;
}

export interface CustomerRow {
  name: string;
  orders: number;
  spent: number;
  lastOrder: string;
}

export interface HeatmapRow {
  day: string;
  hours: number[];
}

export interface FunnelStep {
  stage: string;
  count: number;
  percentage: number;
}

export interface MostViewedProduct {
  id: string;
  name: string;
  image: string;
  views: number;
}

export interface TractionData {
  storeVisits: number;
  uniqueVisitors: number;
  avgSessionDurationSec: number;
  mostViewedProducts: MostViewedProduct[];
  visitsByDay: number[];
}

export interface ReviewItem {
  id: string;
  customer: string;
  rating: number;
  text: string;
  product: string;
  date: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function toDate(val: any): Date {
  if (!val) return new Date(0);
  if (val instanceof Date) return val;
  if (val instanceof Timestamp) return val.toDate();
  if (typeof val === 'object' && typeof val.seconds === 'number') {
    return new Date(val.seconds * 1000);
  }
  if (typeof val === 'number') return new Date(val);
  return new Date(val);
}

function getDateRangeBounds(range: DateRange): { start: Date; end: Date; prevStart: Date; prevEnd: Date } {
  const now = new Date();
  const end = new Date(now);
  let start: Date;
  let msLength: number;

  switch (range) {
    case '7d':
      start = new Date(now);
      start.setDate(start.getDate() - 7);
      msLength = 7 * 24 * 60 * 60 * 1000;
      break;
    case '30d':
      start = new Date(now);
      start.setDate(start.getDate() - 30);
      msLength = 30 * 24 * 60 * 60 * 1000;
      break;
    case '90d':
      start = new Date(now);
      start.setDate(start.getDate() - 90);
      msLength = 90 * 24 * 60 * 60 * 1000;
      break;
    case '12m':
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      msLength = 365 * 24 * 60 * 60 * 1000;
      break;
  }

  start.setHours(0, 0, 0, 0);
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(start.getTime() - msLength);
  prevStart.setHours(0, 0, 0, 0);

  return { start, end, prevStart, prevEnd };
}

const COMPLETED_STATES = ['COMPLETED', 'DELIVERED'];
const TERMINAL_POSITIVE = ['COMPLETED', 'DELIVERED'];
const ALL_NON_DRAFT_STATES = [
  'PAYMENT_AUTHORIZED', 'PENDING_BUSINESS_APPROVAL', 'BUSINESS_APPROVED',
  'DISPATCHING_DRIVER', 'DRIVER_ASSIGNED', 'PICKED_UP',
  'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED',
  'CANCELLED', 'REFUNDED', 'DISPUTED',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CATEGORY_COLORS = [
  'from-purple-400 to-purple-600',
  'from-blue-400 to-blue-600',
  'from-emerald-400 to-emerald-600',
  'from-amber-400 to-amber-600',
  'from-pink-400 to-pink-600',
  'from-gray-400 to-gray-500',
];

function findStateTimestamp(history: Record<string, any> | undefined, targetState: string): number | null {
  if (!history) return null;
  for (const [ts, entry] of Object.entries(flattenStateHistory(history))) {
    if (entry.toState === targetState) return Number(ts);
  }
  return null;
}

function hasReachedState(history: Record<string, any> | undefined, targetState: string): boolean {
  return findStateTimestamp(history, targetState) !== null;
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  return `${diffWeeks}w ago`;
}

// ─── Aggregation functions ───────────────────────────────────────────

function computeRevenueChart(orders: AnalyticsOrder[], range: DateRange, bounds: { start: Date; end: Date }): DailyRevenue[] {
  const completed = orders.filter(o => COMPLETED_STATES.includes(o.state));

  switch (range) {
    case '7d': {
      const buckets = new Map<string, { revenue: number; orders: number }>();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(bounds.end);
        d.setDate(d.getDate() - i);
        const key = DAY_NAMES[d.getDay()];
        if (!buckets.has(key)) buckets.set(key, { revenue: 0, orders: 0 });
      }
      // Use unique keys to avoid duplicate day names
      const dayBuckets: { label: string; revenue: number; orders: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(bounds.end);
        d.setDate(d.getDate() - i);
        dayBuckets.push({ label: DAY_NAMES[d.getDay()], revenue: 0, orders: 0 });
      }
      for (const o of completed) {
        const created = toDate(o.createdAt);
        const dayIndex = Math.floor((bounds.end.getTime() - created.getTime()) / (24 * 60 * 60 * 1000));
        const bucketIdx = 6 - dayIndex;
        if (bucketIdx >= 0 && bucketIdx < 7) {
          dayBuckets[bucketIdx].revenue += o.total;
          dayBuckets[bucketIdx].orders += 1;
        }
      }
      return dayBuckets.map(b => ({ day: b.label, revenue: Math.round(b.revenue * 100) / 100, orders: b.orders }));
    }
    case '30d': {
      const weekBuckets: { label: string; revenue: number; orders: number }[] = [
        { label: 'Wk 1', revenue: 0, orders: 0 },
        { label: 'Wk 2', revenue: 0, orders: 0 },
        { label: 'Wk 3', revenue: 0, orders: 0 },
        { label: 'Wk 4', revenue: 0, orders: 0 },
      ];
      for (const o of completed) {
        const created = toDate(o.createdAt);
        const daysAgo = Math.floor((bounds.end.getTime() - created.getTime()) / (24 * 60 * 60 * 1000));
        const weekIdx = Math.min(3, Math.floor(daysAgo / 7));
        const bucketIdx = 3 - weekIdx;
        if (bucketIdx >= 0 && bucketIdx < 4) {
          weekBuckets[bucketIdx].revenue += o.total;
          weekBuckets[bucketIdx].orders += 1;
        }
      }
      return weekBuckets.map(b => ({ day: b.label, revenue: Math.round(b.revenue * 100) / 100, orders: b.orders }));
    }
    case '90d': {
      const monthBuckets: { label: string; revenue: number; orders: number }[] = [];
      for (let i = 2; i >= 0; i--) {
        const d = new Date(bounds.end);
        d.setMonth(d.getMonth() - i);
        monthBuckets.push({ label: MONTH_NAMES[d.getMonth()], revenue: 0, orders: 0 });
      }
      for (const o of completed) {
        const created = toDate(o.createdAt);
        const monthsAgo = (bounds.end.getFullYear() - created.getFullYear()) * 12 + (bounds.end.getMonth() - created.getMonth());
        const bucketIdx = 2 - monthsAgo;
        if (bucketIdx >= 0 && bucketIdx < 3) {
          monthBuckets[bucketIdx].revenue += o.total;
          monthBuckets[bucketIdx].orders += 1;
        }
      }
      return monthBuckets.map(b => ({ day: b.label, revenue: Math.round(b.revenue * 100) / 100, orders: b.orders }));
    }
    case '12m': {
      const monthBuckets: { label: string; revenue: number; orders: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(bounds.end);
        d.setMonth(d.getMonth() - i);
        monthBuckets.push({ label: MONTH_NAMES[d.getMonth()], revenue: 0, orders: 0 });
      }
      for (const o of completed) {
        const created = toDate(o.createdAt);
        const monthsAgo = (bounds.end.getFullYear() - created.getFullYear()) * 12 + (bounds.end.getMonth() - created.getMonth());
        const bucketIdx = 11 - monthsAgo;
        if (bucketIdx >= 0 && bucketIdx < 12) {
          monthBuckets[bucketIdx].revenue += o.total;
          monthBuckets[bucketIdx].orders += 1;
        }
      }
      return monthBuckets.map(b => ({ day: b.label, revenue: Math.round(b.revenue * 100) / 100, orders: b.orders }));
    }
  }
}

function computeTopProducts(
  currentOrders: AnalyticsOrder[],
  prevOrders: AnalyticsOrder[],
  products: Product[]
): TopProductResult[] {
  const completed = currentOrders.filter(o => COMPLETED_STATES.includes(o.state));
  const prevCompleted = prevOrders.filter(o => COMPLETED_STATES.includes(o.state));

  const productMap = new Map<string, Product>();
  for (const p of products) productMap.set(p.id, p);

  const currentAgg = new Map<string, { unitsSold: number; revenue: number }>();
  for (const o of completed) {
    for (const item of o.items) {
      const existing = currentAgg.get(item.productId) || { unitsSold: 0, revenue: 0 };
      existing.unitsSold += item.quantity;
      existing.revenue += item.price * item.quantity;
      currentAgg.set(item.productId, existing);
    }
  }

  const prevAgg = new Map<string, { unitsSold: number }>();
  for (const o of prevCompleted) {
    for (const item of o.items) {
      const existing = prevAgg.get(item.productId) || { unitsSold: 0 };
      existing.unitsSold += item.quantity;
      prevAgg.set(item.productId, existing);
    }
  }

  const totalOrders = completed.length || 1;
  const results: TopProductResult[] = [];

  for (const [productId, stats] of currentAgg.entries()) {
    const product = productMap.get(productId);
    const prevStats = prevAgg.get(productId);
    const prevSold = prevStats?.unitsSold || 0;
    const trend = prevSold > 0
      ? Math.round(((stats.unitsSold - prevSold) / prevSold) * 100)
      : stats.unitsSold > 0 ? 100 : 0;

    // Conversion rate: orders containing this product / total orders
    const ordersWithProduct = completed.filter(o => o.items.some(i => i.productId === productId)).length;
    const conversionRate = Math.round((ordersWithProduct / totalOrders) * 1000) / 10;

    results.push({
      id: productId,
      name: product?.name || 'Unknown Product',
      image: product?.images?.[0] || '',
      category: product?.category || 'Other',
      unitsSold: stats.unitsSold,
      revenue: Math.round(stats.revenue * 100) / 100,
      trend,
      conversionRate,
    });
  }

  results.sort((a, b) => b.revenue - a.revenue);
  return results.slice(0, 5);
}

function computeCategoryBreakdownFromOrders(
  orders: AnalyticsOrder[],
  products: Product[]
): CategoryRevenue[] {
  const productMap = new Map<string, Product>();
  for (const p of products) productMap.set(p.id, p);

  const catRevMap = new Map<string, number>();
  const completed = orders.filter(o => COMPLETED_STATES.includes(o.state));

  for (const o of completed) {
    for (const item of o.items) {
      const product = productMap.get(item.productId);
      const category = product?.category || 'Other';
      catRevMap.set(category, (catRevMap.get(category) || 0) + item.price * item.quantity);
    }
  }

  const totalRevenue = Array.from(catRevMap.values()).reduce((s, v) => s + v, 0) || 1;
  const sorted = Array.from(catRevMap.entries()).sort((a, b) => b[1] - a[1]);

  return sorted.map(([name, revenue], i) => ({
    name,
    revenue: Math.round(revenue * 100) / 100,
    percentage: Math.round((revenue / totalRevenue) * 100),
    color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
  }));
}

function computePeakHours(orders: AnalyticsOrder[]): HourlyData[] {
  const hourCounts = new Array(24).fill(0);
  for (const o of orders) {
    const hour = toDate(o.createdAt).getHours();
    hourCounts[hour]++;
  }
  // Return hours 8-21 (8am to 9pm)
  const result: HourlyData[] = [];
  for (let h = 8; h <= 21; h++) {
    result.push({ hour: h, orders: hourCounts[h] });
  }
  return result;
}

function computeWeeklyHeatmap(orders: AnalyticsOrder[]): HeatmapRow[] {
  // 7 days x 12 two-hour buckets (8a,10a,12p,...6p,8p,10p = hours 8-21 -> 7 slots,
  // but the mock uses 12 slots for 8a-10p range)
  // We'll use 12 two-hour-ish slots covering 8am-8pm (hours 8-19)
  const matrix: number[][] = Array.from({ length: 7 }, () => new Array(12).fill(0));

  for (const o of orders) {
    const d = toDate(o.createdAt);
    const dayIdx = d.getDay(); // 0=Sun
    const hour = d.getHours();
    // Map hour to bucket: bucket 0 = 8-9, bucket 1 = 10-11, etc.
    const bucket = Math.floor((hour - 8) / 1);
    if (bucket >= 0 && bucket < 12) {
      matrix[dayIdx][bucket]++;
    }
  }

  // Reorder so Mon is first
  const dayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
  return dayOrder.map(dayIdx => ({
    day: DAY_NAMES[dayIdx],
    hours: matrix[dayIdx],
  }));
}

function computeOrderStatusBreakdown(orders: AnalyticsOrder[]): OrderStatusGroup[] {
  const total = orders.length || 1;

  const stateMap: Record<string, string[]> = {
    'Delivered': ['DELIVERED', 'COMPLETED'],
    'In Transit': ['OUT_FOR_DELIVERY', 'PICKED_UP'],
    'Preparing': ['PENDING_BUSINESS_APPROVAL', 'BUSINESS_APPROVED', 'DISPATCHING_DRIVER', 'DRIVER_ASSIGNED'],
    'Cancelled': ['CANCELLED'],
    'Disputed': ['DISPUTED', 'REFUNDED'],
  };

  const colors: Record<string, string> = {
    'Delivered': 'bg-emerald-500',
    'In Transit': 'bg-blue-500',
    'Preparing': 'bg-amber-500',
    'Cancelled': 'bg-red-400',
    'Disputed': 'bg-gray-400',
  };

  return Object.entries(stateMap).map(([label, states]) => ({
    label,
    count: orders.filter(o => states.includes(o.state)).length,
    total,
    color: colors[label],
  }));
}

function computeCustomerSegments(
  currentOrders: AnalyticsOrder[],
  allOrders: AnalyticsOrder[]
): { segments: CustomerSegment[]; totalCustomers: number; retentionRate: number; avgLtv: number } {
  // Lifetime order counts by shopper (from all orders)
  const lifetimeCounts = new Map<string, number>();
  for (const o of allOrders) {
    if (COMPLETED_STATES.includes(o.state) || ALL_NON_DRAFT_STATES.includes(o.state)) {
      lifetimeCounts.set(o.shopperId, (lifetimeCounts.get(o.shopperId) || 0) + 1);
    }
  }

  // Current period unique shoppers
  const currentShoppers = new Set<string>();
  for (const o of currentOrders) {
    currentShoppers.add(o.shopperId);
  }

  let newCount = 0;
  let returningCount = 0;
  let loyalCount = 0;

  for (const shopperId of currentShoppers) {
    const lifetime = lifetimeCounts.get(shopperId) || 0;
    if (lifetime >= 5) loyalCount++;
    else if (lifetime >= 2) returningCount++;
    else newCount++;
  }

  const totalCustomers = currentShoppers.size || 1;
  const segments: CustomerSegment[] = [
    { label: 'New Customers', count: newCount, percentage: Math.round((newCount / totalCustomers) * 100), color: 'bg-purple-500' },
    { label: 'Returning', count: returningCount, percentage: Math.round((returningCount / totalCustomers) * 100), color: 'bg-blue-500' },
    { label: 'Loyal (5+ orders)', count: loyalCount, percentage: Math.round((loyalCount / totalCustomers) * 100), color: 'bg-emerald-500' },
  ];

  const retentionRate = totalCustomers > 0
    ? Math.round(((returningCount + loyalCount) / totalCustomers) * 100)
    : 0;

  const totalRevenue = currentOrders
    .filter(o => COMPLETED_STATES.includes(o.state))
    .reduce((s, o) => s + o.total, 0);
  const avgLtv = currentShoppers.size > 0 ? Math.round(totalRevenue / currentShoppers.size) : 0;

  return { segments, totalCustomers: currentShoppers.size, retentionRate, avgLtv };
}

function computeTopCustomers(orders: AnalyticsOrder[]): CustomerRow[] {
  const customerMap = new Map<string, { name: string; orders: number; spent: number; lastOrderDate: Date }>();

  for (const o of orders) {
    if (!COMPLETED_STATES.includes(o.state) && !ALL_NON_DRAFT_STATES.includes(o.state)) continue;
    const existing = customerMap.get(o.shopperId);
    const created = toDate(o.createdAt);
    if (existing) {
      existing.orders++;
      existing.spent += o.total;
      if (created > existing.lastOrderDate) {
        existing.lastOrderDate = created;
        if (o.customerName) existing.name = o.customerName;
      }
    } else {
      customerMap.set(o.shopperId, {
        name: o.customerName || 'Customer',
        orders: 1,
        spent: o.total,
        lastOrderDate: created,
      });
    }
  }

  const rows = Array.from(customerMap.values())
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 6)
    .map(c => ({
      name: c.name,
      orders: c.orders,
      spent: Math.round(c.spent * 100) / 100,
      lastOrder: formatTimeAgo(c.lastOrderDate),
    }));

  return rows;
}

function computePerformanceMetrics(orders: AnalyticsOrder[]): PerformanceMetric[] {
  const totalSubmitted = orders.filter(o =>
    hasReachedState(o.stateHistory, 'PENDING_BUSINESS_APPROVAL')
  ).length;
  const totalApproved = orders.filter(o =>
    hasReachedState(o.stateHistory, 'BUSINESS_APPROVED')
  ).length;
  const acceptanceRate = totalSubmitted > 0
    ? Math.round((totalApproved / totalSubmitted) * 1000) / 10
    : 100;

  // Avg prep time: PENDING_BUSINESS_APPROVAL -> BUSINESS_APPROVED
  const prepTimes: number[] = [];
  for (const o of orders) {
    const submittedTs = findStateTimestamp(o.stateHistory, 'PENDING_BUSINESS_APPROVAL');
    const approvedTs = findStateTimestamp(o.stateHistory, 'BUSINESS_APPROVED');
    if (submittedTs && approvedTs && approvedTs > submittedTs) {
      prepTimes.push((approvedTs - submittedTs) / 60000); // minutes
    }
  }
  const avgPrepTime = prepTimes.length > 0
    ? Math.round((prepTimes.reduce((s, t) => s + t, 0) / prepTimes.length) * 10) / 10
    : 0;

  // On-time pickup: orders that reached PICKED_UP / orders that reached DRIVER_ASSIGNED
  const driverAssigned = orders.filter(o => hasReachedState(o.stateHistory, 'DRIVER_ASSIGNED')).length;
  const pickedUp = orders.filter(o => hasReachedState(o.stateHistory, 'PICKED_UP')).length;
  const pickupRate = driverAssigned > 0
    ? Math.round((pickedUp / driverAssigned) * 1000) / 10
    : 100;

  // Return rate
  const completedOrders = orders.filter(o => COMPLETED_STATES.includes(o.state));
  const ordersWithReturns = completedOrders.filter(o => o.returns?.hasActiveReturn).length;
  const returnRate = completedOrders.length > 0
    ? Math.round((ordersWithReturns / completedOrders.length) * 1000) / 10
    : 0;

  return [
    {
      label: 'Order Acceptance Rate',
      value: `${acceptanceRate}%`,
      target: '95%',
      achieved: acceptanceRate >= 95,
      barWidth: acceptanceRate,
    },
    {
      label: 'Avg. Prep Time',
      value: avgPrepTime > 0 ? `${avgPrepTime} min` : 'N/A',
      target: '< 10 min',
      achieved: avgPrepTime > 0 && avgPrepTime < 10,
      barWidth: avgPrepTime > 0 ? Math.min((avgPrepTime / 15) * 100, 100) : 0,
    },
    {
      label: 'Pickup Rate',
      value: `${pickupRate}%`,
      target: '90%',
      achieved: pickupRate >= 90,
      barWidth: pickupRate,
    },
    {
      label: 'Return Rate',
      value: `${returnRate}%`,
      target: '< 5%',
      achieved: returnRate < 5,
      barWidth: Math.min(returnRate * 10, 100),
    },
  ];
}

function computeFunnel(
  orders: AnalyticsOrder[],
  eventCounts: { pageViews: number; addToBag: number }
): FunnelStep[] {
  const checkoutStarted = orders.length;
  const ordersPlaced = orders.filter(o =>
    ALL_NON_DRAFT_STATES.includes(o.state)
  ).length;
  const delivered = orders.filter(o =>
    COMPLETED_STATES.includes(o.state)
  ).length;

  const maxCount = eventCounts.pageViews || checkoutStarted || 1;

  const steps: FunnelStep[] = [];
  if (eventCounts.pageViews > 0) {
    steps.push({ stage: 'Page Views', count: eventCounts.pageViews, percentage: 100 });
  }
  if (eventCounts.addToBag > 0) {
    steps.push({ stage: 'Added to Bag', count: eventCounts.addToBag, percentage: Math.round((eventCounts.addToBag / maxCount) * 1000) / 10 });
  }
  steps.push({ stage: 'Checkout Started', count: checkoutStarted, percentage: Math.round((checkoutStarted / maxCount) * 1000) / 10 });
  steps.push({ stage: 'Orders Placed', count: ordersPlaced, percentage: Math.round((ordersPlaced / maxCount) * 1000) / 10 });
  steps.push({ stage: 'Delivered', count: delivered, percentage: Math.round((delivered / maxCount) * 1000) / 10 });

  // If no page views tracked yet, renormalize so first step is 100%
  if (steps.length > 0 && steps[0].percentage !== 100) {
    const firstCount = steps[0].count || 1;
    for (const s of steps) {
      s.percentage = Math.round((s.count / firstCount) * 1000) / 10;
    }
  }

  return steps;
}

function computeLowStock(products: Product[]): LowStockItem[] {
  const items: LowStockItem[] = [];
  for (const product of products) {
    if (!product.active) continue;
    const threshold = product.lowStockThreshold || 3;
    for (const size of product.sizes) {
      const sellable = size.stockCount - size.reservedCount;
      if (sellable <= threshold && sellable >= 0) {
        items.push({
          name: product.name,
          size: size.label,
          remaining: Math.max(0, sellable),
          image: product.images?.[0] || '',
        });
      }
    }
  }
  items.sort((a, b) => a.remaining - b.remaining);
  return items.slice(0, 8);
}

function buildSparkline(orders: AnalyticsOrder[], keyFn: (o: AnalyticsOrder) => number, bounds: { start: Date; end: Date }): number[] {
  const totalMs = bounds.end.getTime() - bounds.start.getTime();
  const bucketMs = totalMs / 7;
  const buckets = new Array(7).fill(0);

  for (const o of orders) {
    const t = toDate(o.createdAt).getTime();
    const idx = Math.min(6, Math.floor((t - bounds.start.getTime()) / bucketMs));
    if (idx >= 0 && idx < 7) buckets[idx] += keyFn(o);
  }
  return buckets;
}

// ─── Main Hook ───────────────────────────────────────────────────────

export function useBusinessAnalytics(businessId: string | undefined, dateRange: DateRange) {
  const [currentOrders, setCurrentOrders] = useState<AnalyticsOrder[]>([]);
  const [prevOrders, setPrevOrders] = useState<AnalyticsOrder[]>([]);
  const [allOrders, setAllOrders] = useState<AnalyticsOrder[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [eventCounts, setEventCounts] = useState<{ pageViews: number; addToBag: number }>({ pageViews: 0, addToBag: 0 });
  const [traction, setTraction] = useState<TractionData>({
    storeVisits: 0,
    uniqueVisitors: 0,
    avgSessionDurationSec: 0,
    mostViewedProducts: [],
    visitsByDay: [0, 0, 0, 0, 0, 0, 0],
  });
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [businessRating, setBusinessRating] = useState<number>(0);
  const [businessReviewCount, setBusinessReviewCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bounds = useMemo(() => getDateRangeBounds(dateRange), [dateRange]);

  // Fetch current period orders (real-time)
  useEffect(() => {
    if (!businessId) { setLoading(false); return; }

    const q = query(
      collection(db, 'orders'),
      where('businessId', '==', businessId),
      where('createdAt', '>=', bounds.start),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, snap => {
      setCurrentOrders(snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalyticsOrder)));
      setLoading(false);
    }, err => {
      console.error('Analytics orders error:', err);
      setError(err.message);
      setLoading(false);
    });

    return () => unsub();
  }, [businessId, bounds.start]);

  // Fetch all orders for this business (for lifetime customer segmentation)
  useEffect(() => {
    if (!businessId) return;

    const q = query(
      collection(db, 'orders'),
      where('businessId', '==', businessId),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, snap => {
      setAllOrders(snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalyticsOrder)));
    }, err => {
      console.error('Analytics all-orders error:', err);
    });

    return () => unsub();
  }, [businessId]);

  // Fetch previous period orders (one-time, for comparison)
  useEffect(() => {
    if (!businessId) return;

    const fetchPrev = async () => {
      try {
        const q = query(
          collection(db, 'orders'),
          where('businessId', '==', businessId),
          where('createdAt', '>=', bounds.prevStart),
          where('createdAt', '<=', bounds.prevEnd),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setPrevOrders(snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalyticsOrder)));
      } catch (err: any) {
        console.error('Analytics prev-period error:', err);
      }
    };
    fetchPrev();
  }, [businessId, bounds.prevStart, bounds.prevEnd]);

  // Fetch products
  useEffect(() => {
    if (!businessId) return;

    const q = query(
      collection(db, 'products'),
      where('brandId', '==', businessId),
      where('active', '==', true)
    );

    const unsub = onSnapshot(q, snap => {
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() } as Product)));
    }, err => {
      console.error('Analytics products error:', err);
    });

    return () => unsub();
  }, [businessId]);

  // Fetch business rating
  useEffect(() => {
    if (!businessId) return;

    const unsub = onSnapshot(doc(db, 'businesses', businessId), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setBusinessRating(data.rating || 0);
        setBusinessReviewCount(data.reviewCount || 0);
      }
    });

    return () => unsub();
  }, [businessId]);

  // Fetch recent reviews from subcollection
  useEffect(() => {
    if (!businessId) return;

    const q = query(
      collection(db, 'businesses', businessId, 'reviews'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, snap => {
      const items: ReviewItem[] = snap.docs.slice(0, 10).map(d => {
        const data = d.data();
        return {
          id: d.id,
          customer: data.customerName || 'Customer',
          rating: data.rating || 5,
          text: data.text || '',
          product: data.productName || '',
          date: data.createdAt ? formatTimeAgo(toDate(data.createdAt)) : '',
        };
      });
      setReviews(items);
    }, () => {
      // subcollection may not exist yet
      setReviews([]);
    });

    return () => unsub();
  }, [businessId]);

  // Fetch analytics events (page views, add-to-bag) for funnel
  useEffect(() => {
    if (!businessId) return;

    const fetchEvents = async () => {
      try {
        const pvQuery = query(
          collection(db, 'analytics_events'),
          where('businessId', '==', businessId),
          where('type', '==', 'page_view'),
          where('timestamp', '>=', bounds.start)
        );
        const atbQuery = query(
          collection(db, 'analytics_events'),
          where('businessId', '==', businessId),
          where('type', '==', 'add_to_bag'),
          where('timestamp', '>=', bounds.start)
        );

        const [pvSnap, atbSnap] = await Promise.all([
          getDocs(pvQuery),
          getDocs(atbQuery),
        ]);
        setEventCounts({ pageViews: pvSnap.size, addToBag: atbSnap.size });
      } catch (err: any) {
        // analytics_events collection may not exist yet -- that's fine
        console.debug('Analytics events query skipped:', err.message);
        setEventCounts({ pageViews: 0, addToBag: 0 });
      }
    };
    fetchEvents();
  }, [businessId, bounds.start]);

  // Fetch traction data: product views, sessions, visitor info
  useEffect(() => {
    if (!businessId) return;

    const fetchTraction = async () => {
      try {
        const productViewQuery = query(
          collection(db, 'analytics_events'),
          where('businessId', '==', businessId),
          where('type', '==', 'product_view'),
          where('timestamp', '>=', bounds.start)
        );
        const pageViewQuery = query(
          collection(db, 'analytics_events'),
          where('businessId', '==', businessId),
          where('type', '==', 'page_view'),
          where('timestamp', '>=', bounds.start)
        );
        const sessionQuery = query(
          collection(db, 'analytics_events'),
          where('businessId', '==', businessId),
          where('type', '==', 'session_end'),
          where('timestamp', '>=', bounds.start)
        );

        const [pvSnap, pageSnap, sessionSnap] = await Promise.all([
          getDocs(productViewQuery),
          getDocs(pageViewQuery),
          getDocs(sessionQuery),
        ]);

        // Store visits & unique visitors
        const storeVisits = pageSnap.size;
        const visitorIds = new Set<string>();
        const visitsByDay = new Array(7).fill(0);
        pageSnap.docs.forEach(d => {
          const data = d.data();
          if (data.shopperId) visitorIds.add(data.shopperId);
          const ts = toDate(data.timestamp);
          const daysAgo = Math.floor((bounds.end.getTime() - ts.getTime()) / (24 * 60 * 60 * 1000));
          const bucketIdx = 6 - daysAgo;
          if (bucketIdx >= 0 && bucketIdx < 7) visitsByDay[bucketIdx]++;
        });

        // Most viewed products
        const viewCounts = new Map<string, number>();
        pvSnap.docs.forEach(d => {
          const productId = d.data().productId;
          if (productId) viewCounts.set(productId, (viewCounts.get(productId) || 0) + 1);
        });
        const sortedViews = Array.from(viewCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);
        const mostViewedProducts: MostViewedProduct[] = sortedViews.map(([id, views]) => {
          const product = products.find(p => p.id === id);
          return {
            id,
            name: product?.name || 'Unknown Product',
            image: product?.images?.[0] || '',
            views,
          };
        });

        // Avg session duration
        let totalDurationMs = 0;
        let sessionCount = 0;
        sessionSnap.docs.forEach(d => {
          const dur = d.data().metadata?.durationMs;
          if (typeof dur === 'number' && dur > 0) {
            totalDurationMs += dur;
            sessionCount++;
          }
        });
        const avgSessionDurationSec = sessionCount > 0
          ? Math.round(totalDurationMs / sessionCount / 1000)
          : 0;

        setTraction({
          storeVisits,
          uniqueVisitors: visitorIds.size,
          avgSessionDurationSec,
          mostViewedProducts,
          visitsByDay,
        });
      } catch (err: any) {
        console.debug('Traction data query skipped:', err.message);
      }
    };
    fetchTraction();
  }, [businessId, bounds.start, bounds.end, products]);

  // ─── Computed analytics ────────────────────────────────────────────

  const analytics = useMemo(() => {
    const currentCompleted = currentOrders.filter(o => COMPLETED_STATES.includes(o.state));
    const prevCompleted = prevOrders.filter(o => COMPLETED_STATES.includes(o.state));

    // KPIs
    const totalRevenue = currentCompleted.reduce((s, o) => s + o.total, 0);
    const prevRevenue = prevCompleted.reduce((s, o) => s + o.total, 0);
    const revenueChange = prevRevenue > 0
      ? Math.round(((totalRevenue - prevRevenue) / prevRevenue) * 1000) / 10
      : totalRevenue > 0 ? 100 : 0;

    const orderCount = currentCompleted.length;
    const prevOrderCount = prevCompleted.length;
    const orderChange = prevOrderCount > 0
      ? Math.round(((orderCount - prevOrderCount) / prevOrderCount) * 1000) / 10
      : orderCount > 0 ? 100 : 0;

    const avgOrderValue = orderCount > 0 ? Math.round((totalRevenue / orderCount) * 100) / 100 : 0;
    const prevAov = prevOrderCount > 0 ? prevRevenue / prevOrderCount : 0;
    const aovChange = prevAov > 0
      ? Math.round(((avgOrderValue - prevAov) / prevAov) * 1000) / 10
      : 0;

    const revenueSparkline = buildSparkline(currentCompleted, o => o.total, bounds);
    const ordersSparkline = buildSparkline(currentCompleted, () => 1, bounds);
    const aovSparkline = revenueSparkline.map((rev, i) =>
      ordersSparkline[i] > 0 ? Math.round(rev / ordersSparkline[i]) : 0
    );

    const kpis: KPIResult[] = [
      {
        label: 'Total Revenue',
        value: `$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
        change: revenueChange,
        changeLabel: 'vs last period',
        sparkline: revenueSparkline,
      },
      {
        label: 'Orders',
        value: `${orderCount}`,
        change: orderChange,
        changeLabel: 'vs last period',
        sparkline: ordersSparkline,
      },
      {
        label: 'Avg. Order Value',
        value: `$${avgOrderValue.toFixed(2)}`,
        change: aovChange,
        changeLabel: 'vs last period',
        sparkline: aovSparkline,
      },
      {
        label: 'Customer Rating',
        value: businessRating > 0 ? businessRating.toFixed(1) : 'N/A',
        change: 0,
        changeLabel: `${businessReviewCount} reviews`,
        sparkline: [businessRating, businessRating, businessRating, businessRating, businessRating, businessRating, businessRating],
      },
    ];

    // Orders tab KPIs
    const completionRate = currentOrders.length > 0
      ? Math.round((currentCompleted.length / currentOrders.length) * 1000) / 10
      : 0;
    const prevCompletionRate = prevOrders.length > 0
      ? Math.round((prevCompleted.length / prevOrders.length) * 1000) / 10
      : 0;

    const refundedOrders = currentOrders.filter(o => o.state === 'REFUNDED' || o.returns?.hasActiveReturn).length;
    const refundRate = currentOrders.length > 0
      ? Math.round((refundedOrders / currentOrders.length) * 1000) / 10
      : 0;

    // Prep time for orders KPI
    const prepTimes: number[] = [];
    for (const o of currentOrders) {
      const submittedTs = findStateTimestamp(o.stateHistory, 'PENDING_BUSINESS_APPROVAL');
      const approvedTs = findStateTimestamp(o.stateHistory, 'BUSINESS_APPROVED');
      if (submittedTs && approvedTs) {
        prepTimes.push((approvedTs - submittedTs) / 60000);
      }
    }
    const avgPrepTime = prepTimes.length > 0
      ? Math.round((prepTimes.reduce((s, t) => s + t, 0) / prepTimes.length) * 10) / 10
      : 0;

    const orderKpis: KPIResult[] = [
      {
        label: 'Total Orders',
        value: `${currentOrders.length}`,
        change: orderChange,
        changeLabel: 'vs last period',
        sparkline: buildSparkline(currentOrders, () => 1, bounds),
      },
      {
        label: 'Completion Rate',
        value: `${completionRate}%`,
        change: Math.round((completionRate - prevCompletionRate) * 10) / 10,
        changeLabel: 'vs last period',
        sparkline: [completionRate, completionRate, completionRate, completionRate, completionRate, completionRate, completionRate],
      },
      {
        label: 'Avg. Prep Time',
        value: avgPrepTime > 0 ? `${avgPrepTime} min` : 'N/A',
        change: 0,
        changeLabel: '',
        sparkline: prepTimes.length > 0 ? prepTimes.slice(-7) : [0, 0, 0, 0, 0, 0, 0],
      },
      {
        label: 'Refund Rate',
        value: `${refundRate}%`,
        change: 0,
        changeLabel: '',
        sparkline: [refundRate, refundRate, refundRate, refundRate, refundRate, refundRate, refundRate],
      },
    ];

    // Products tab KPIs
    const activeProductCount = products.length;
    const totalUnitsSold = currentCompleted.reduce(
      (s, o) => s + o.items.reduce((is, item) => is + item.quantity, 0), 0
    );
    const lowStockItems = computeLowStock(products);

    const productKpis: KPIResult[] = [
      {
        label: 'Active Products',
        value: `${activeProductCount}`,
        change: 0,
        changeLabel: '',
        sparkline: [activeProductCount, activeProductCount, activeProductCount, activeProductCount, activeProductCount, activeProductCount, activeProductCount],
      },
      {
        label: 'Total Units Sold',
        value: `${totalUnitsSold}`,
        change: 0,
        changeLabel: 'this period',
        sparkline: buildSparkline(currentCompleted, o => o.items.reduce((s, i) => s + i.quantity, 0), bounds),
      },
      {
        label: 'Avg. Conversion',
        value: 'N/A',
        change: 0,
        changeLabel: 'needs page view tracking',
        sparkline: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        label: 'Low Stock Items',
        value: `${lowStockItems.length}`,
        change: 0,
        changeLabel: '',
        sparkline: [lowStockItems.length, lowStockItems.length, lowStockItems.length, lowStockItems.length, lowStockItems.length, lowStockItems.length, lowStockItems.length],
      },
    ];

    // Customer tab KPIs
    const { segments: customerSegments, totalCustomers, retentionRate, avgLtv } = computeCustomerSegments(currentOrders, allOrders);

    const customerKpis: KPIResult[] = [
      {
        label: 'Total Customers',
        value: `${totalCustomers}`,
        change: 0,
        changeLabel: 'this period',
        sparkline: buildSparkline(currentOrders, () => 1, bounds),
      },
      {
        label: 'Repeat Rate',
        value: `${retentionRate}%`,
        change: 0,
        changeLabel: '',
        sparkline: [retentionRate, retentionRate, retentionRate, retentionRate, retentionRate, retentionRate, retentionRate],
      },
      {
        label: 'Avg. Rating',
        value: businessRating > 0 ? businessRating.toFixed(1) : 'N/A',
        change: 0,
        changeLabel: '',
        sparkline: [businessRating, businessRating, businessRating, businessRating, businessRating, businessRating, businessRating],
      },
      {
        label: 'Lifetime Value',
        value: avgLtv > 0 ? `$${avgLtv}` : 'N/A',
        change: 0,
        changeLabel: '',
        sparkline: [avgLtv, avgLtv, avgLtv, avgLtv, avgLtv, avgLtv, avgLtv],
      },
    ];

    // Charts and cards
    const revenueChart = computeRevenueChart(currentOrders, dateRange, bounds);
    const topProducts = computeTopProducts(currentOrders, prevOrders, products);
    const categoryBreakdown = computeCategoryBreakdownFromOrders(currentOrders, products);
    const peakHours = computePeakHours(currentOrders);
    const weeklyHeatmap = computeWeeklyHeatmap(currentOrders);
    const orderStatusBreakdown = computeOrderStatusBreakdown(currentOrders);
    const topCustomers = computeTopCustomers(allOrders);
    const performance = computePerformanceMetrics(currentOrders);
    const funnel = computeFunnel(currentOrders, eventCounts);

    // Order sub-metrics
    const avgItemsPerOrder = currentCompleted.length > 0
      ? Math.round((currentCompleted.reduce((s, o) => s + o.items.length, 0) / currentCompleted.length) * 10) / 10
      : 0;

    // Avg fulfillment time (BUSINESS_APPROVED -> DELIVERED)
    const fulfillTimes: number[] = [];
    for (const o of currentCompleted) {
      const approvedTs = findStateTimestamp(o.stateHistory, 'BUSINESS_APPROVED');
      const deliveredTs = findStateTimestamp(o.stateHistory, 'DELIVERED');
      if (approvedTs && deliveredTs) {
        fulfillTimes.push((deliveredTs - approvedTs) / 60000);
      }
    }
    const avgFulfillmentTime = fulfillTimes.length > 0
      ? Math.round(fulfillTimes.reduce((s, t) => s + t, 0) / fulfillTimes.length)
      : 0;

    return {
      kpis,
      orderKpis,
      productKpis,
      customerKpis,
      revenueChart,
      topProducts,
      categoryBreakdown,
      peakHours,
      weeklyHeatmap,
      orderStatusBreakdown,
      lowStockItems,
      customerSegments,
      totalCustomers,
      retentionRate,
      avgLtv,
      topCustomers,
      performance,
      funnel,
      avgOrderValue,
      avgItemsPerOrder,
      avgFulfillmentTime,
    };
  }, [currentOrders, prevOrders, allOrders, products, businessRating, businessReviewCount, eventCounts, dateRange, bounds]);

  return { ...analytics, traction, reviews, loading, error };
}
